import type { FC } from 'react';
import { Breakpoint, ADBRIDG_CLASS } from './types';
import {
    adWrapperStyle,
    moreBelowStyle,
    stickyIntersitialWrapperStyle
} from './AdbridgSlot.css';
import { bpVariant } from './breakpoints';

/* To help avoid cumulative layout shift we should set a reasonable min height on the ad slots */
const MIN_HEIGHTS: Record<ADBRIDG_CLASS, number> = {
    [ADBRIDG_CLASS.TEXT_LINK_LSRP]: 108,
    [ADBRIDG_CLASS.MPU]: 250,
    [ADBRIDG_CLASS.VALUATION_BOOSTER]: 50,
    [ADBRIDG_CLASS.MPU_2]: 250,
    [ADBRIDG_CLASS.SKYSCRAPER]: 0 /* doesn't matter as sticky */,
    [ADBRIDG_CLASS.SKYSCRAPER_2]: 0 /* doesn't matter as sticky */,
    [ADBRIDG_CLASS.INTERSTITIAL_DESKTOP]: 440,
    [ADBRIDG_CLASS.INTERSTITIAL_MOBILE]: 755,
    [ADBRIDG_CLASS.SPONSOR_MOBILE]: 0,
    [ADBRIDG_CLASS.TEXT_LINK_LDP]: 0,
    [ADBRIDG_CLASS.SPONSOR_DESKTOP]: 0,
    [ADBRIDG_CLASS.TEXT_LINK_MH]: 0,
    [ADBRIDG_CLASS.TEXT_LINK]: 0,
    [ADBRIDG_CLASS.INLINE_SPECIAL]: 108,
    [ADBRIDG_CLASS.ADB_FLUID]: 0,
    [ADBRIDG_CLASS.ADB_TEXT_LINK]: 108,
    [ADBRIDG_CLASS.ADB_SPECIAL_BANNER]: 369,
    [ADBRIDG_CLASS.ADB_HP_SPECIAL_BANNER]: 369
};

const UnitWrapper: FC<
    React.PropsWithChildren<{ stickyIntersitial: boolean }>
> = ({ stickyIntersitial, children }) =>
    stickyIntersitial ? (
        <>
            <p className={moreBelowStyle}>More results below</p>
            <div className={stickyIntersitialWrapperStyle}>{children}</div>
        </>
    ) : (
        children
    );

export const AdbridgSlot: FC<{
    adbridgClass?: string;
    adbridgDataClass?: string;
    hasMarginTop?: boolean;
    height?: number;
    showOnly?: Breakpoint;
}> = ({
    adbridgClass,
    adbridgDataClass,
    hasMarginTop = true,
    height,
    showOnly
}) => {
    const resolvedClass =
        adbridgDataClass === ADBRIDG_CLASS.ADB_TEXT_LINK
            ? adbridgDataClass
            : adbridgClass;

    const minHeight =
        // We can safely cast here because if resolvedClass isn't a valid key of
        // MIN_HEIGHTS then this will resolve as 0 anyway.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        height ?? MIN_HEIGHTS[resolvedClass as ADBRIDG_CLASS] ?? 0;

    const stickyInterstitial =
        resolvedClass === ADBRIDG_CLASS.INTERSTITIAL_MOBILE ||
        resolvedClass === ADBRIDG_CLASS.INTERSTITIAL_DESKTOP;

    return (
        <div
            className={adWrapperStyle({
                breakpoint: bpVariant(showOnly),
                stickyInterstitial,
                marginTop: hasMarginTop
            })}
            style={{ height, minHeight }}
        >
            <UnitWrapper stickyIntersitial={stickyInterstitial}>
                <div
                    itemScope={true}
                    itemType="https://schema.org/WPAdBlock"
                    className="zad-slot"
                    style={{
                        width: '100%',
                        overflow: 'hidden'
                    }}
                    data-adbridg-ad-class={resolvedClass}
                    data-testid={`zad_${resolvedClass}`}
                />
            </UnitWrapper>
        </div>
    );
};
